import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Check, Coins, CreditCard, TriangleAlert } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/shadcn-io/spinner'
import { cn } from '@/lib/utils'
import {
    aiErrorMessage,
    cancelAISubscription,
    checkoutAIPackage,
    formatDate,
    formatMoney,
    formatTokens,
    getAIPackages,
    getAISubscription,
    openBillingPortal,
    subscribeToPlan,
    aiErrorCode,
    type AIPackage,
    type AIPlan,
    type AIPlanChange,
    type AISubscription,
    type BillingInterval,
    PENDING_PURCHASE_KEY,
} from '@/lib/ai'
import { useBilling, type PricingTab } from '@/components/billing/billingContext'

const INTERVAL_LABEL: Record<BillingInterval, string> = { month: 'month', year: 'year' }

/** Plans are ranked on what they cost a month, whatever they bill. */
const monthly = (plan: Pick<AIPlan, 'price' | 'billingInterval'>) => plan.price / (plan.billingInterval === 'year' ? 12 : 1)

const MONTHS: Record<BillingInterval, number> = { month: 1, year: 12 }

/** Stripe will not charge less than this, so the discount stops short of it. */
const MIN_CHARGE = 2

/**
 * What choosing a plan would do. The API sends this with each plan
 * (`plan.change`) and that is what is charged; this works it out the same way
 * only for an API that does not send it yet. A longer interval (month to
 * year) is always an upgrade; a cheaper plan with a shorter one is refused.
 */
const fallbackChange = (plan: AIPlan, subscription: AISubscription | null): AIPlanChange => {
    const current = subscription?.plan ?? null
    if (!current) return { kind: 'new', chargeNow: plan.price, discount: 0 }
    if (current.id === plan.id) return { kind: 'current', chargeNow: 0, discount: 0 }
    if (subscription?.status === 'past_due') {
        return { kind: 'blocked', message: 'Your last payment did not go through. Update your payment method before changing plan.' }
    }
    const longer = MONTHS[plan.billingInterval] > MONTHS[current.billingInterval]
    const shorter = MONTHS[plan.billingInterval] < MONTHS[current.billingInterval]
    if (longer || monthly(plan) > monthly(current)) {
        const unused = Math.max(0, Number(subscription?.unused?.value ?? 0))
        const discount = Math.max(0, Math.min(Math.round(unused * 100), Math.round(plan.price * 100) - MIN_CHARGE * 100)) / 100
        return {
            kind: 'upgrade',
            chargeNow: Math.round((plan.price - discount) * 100) / 100,
            discount,
            unusedTokens: discount > 0 ? Number(subscription?.unused?.tokens ?? 0) : 0,
            startsNewPeriod: true,
        }
    }
    if (shorter) return { kind: 'blocked', message: 'To move to a cheaper plan with different billing, cancel your plan and choose the new one once it ends.' }
    return { kind: 'downgrade', chargeNow: 0, discount: 0, effectiveFrom: subscription?.currentPeriodEnd ?? null }
}

const changeFor = (plan: AIPlan, subscription: AISubscription | null): AIPlanChange => plan.change ?? fallbackChange(plan, subscription)

/** Plan and checkout errors in plain words; anything else as the API says it. */
const billingError = (err: unknown) => {
    const code = aiErrorCode(err)
    if (code === 'AI_PACKAGE_UNAVAILABLE') return 'That plan is not on sale right now. The list has been refreshed.'
    if (code === 'AI_CHECKOUT_IN_PROGRESS') return 'A checkout is already open. Finish it or close it, then try again.'
    return aiErrorMessage(err)
}

/** When a plan bought now first renews: one billing period from today. */
const renewsFromToday = (interval: BillingInterval) => {
    const date = new Date()
    if (interval === 'year') date.setFullYear(date.getFullYear() + 1)
    else date.setMonth(date.getMonth() + 1)
    return date.toISOString()
}

/** `RM` and the amount apart, the way the price is set on the card. */
const splitMoney = (amount: number | string, currency: string) => {
    const [prefix, value] = formatMoney(amount, currency).split(' ')
    return { prefix, value: value.endsWith('.00') ? value.slice(0, -3) : value }
}

type Pending = { plan: AIPlan; change: AIPlanChange } | { cancel: true }

/**
 * Plans and top-up credits, on their own page at /pricing (`?tab=credits` for
 * the top-ups), reached from the profile menu or any "buy more tokens" prompt,
 * so the browser's Back button leaves it. A first plan and every top-up go
 * through Stripe
 * Checkout; changing or cancelling a plan happens here, after a confirmation
 * that says what will be charged and when.
 */
const PricingPage = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const tab: PricingTab = searchParams.get('tab') === 'credits' ? 'credits' : 'plans'
    const onTabChange = (next: PricingTab) => setSearchParams(next === 'plans' ? {} : { tab: next }, { replace: true })
    const { setSubscription: onSubscriptionChange, refreshBalance } = useBilling()
    const [loading, setLoading] = useState(true)
    const [plans, setPlans] = useState<AIPlan[]>([])
    const [packages, setPackages] = useState<AIPackage[]>([])
    const [subscription, setSubscription] = useState<AISubscription | null>(null)
    const [enabled, setEnabled] = useState(false)
    const [billing, setBilling] = useState<BillingInterval>('month')
    const [busy, setBusy] = useState<string | null>(null)
    const [pending, setPending] = useState<Pending | null>(null)
    /** An upgrade waiting on the bank: the Stripe page it is finished on. */
    const [awaitingPayment, setAwaitingPayment] = useState<{ url: string | null; planId: number } | null>(null)
    /** Bumped to load the plans again, as when one has gone off sale. */
    const [reloadPlans, setReloadPlans] = useState(0)

    // Coming back from Stripe with Back (button, trackpad or mouse) restores
    // this page as it was left, with a button still spinning, so it is reset.
    useEffect(() => {
        const restore = () => setBusy(null)
        window.addEventListener('pageshow', restore)
        return () => window.removeEventListener('pageshow', restore)
    }, [])

    const applySubscription = useCallback(
        (next: AISubscription | null) => {
            setSubscription(next)
            onSubscriptionChange(next)
        },
        [onSubscriptionChange],
    )

    /** The plan and balance afresh, after anything that may have changed them. */
    const reloadSubscription = useCallback(async () => {
        refreshBalance()
        try {
            const { subscription: next } = await getAISubscription()
            applySubscription(next)
            return next
        } catch {
            return null
        }
    }, [applySubscription, refreshBalance])

    // Back from finishing a payment on Stripe in another tab: see whether the
    // plan has changed. The page itself is left as it was.
    useEffect(() => {
        if (!awaitingPayment) return
        const check = () => {
            if (document.visibilityState !== 'visible') return
            reloadSubscription().then((next) => {
                if (next?.plan?.id === awaitingPayment.planId) {
                    setAwaitingPayment(null)
                    toast.success(`You're on ${next.plan.name}.`)
                }
            })
        }
        window.addEventListener('focus', check)
        document.addEventListener('visibilitychange', check)
        return () => {
            window.removeEventListener('focus', check)
            document.removeEventListener('visibilitychange', check)
        }
    }, [awaitingPayment, reloadSubscription])

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        getAIPackages()
            .then((data) => {
                if (cancelled) return
                setPlans(data.plans || [])
                setPackages(data.packages || [])
                setEnabled(Boolean(data.purchases?.enabled))
                applySubscription(data.subscription)
                const intervals = new Set((data.plans || []).map((p) => p.billingInterval))
                setBilling(data.subscription?.plan?.billingInterval ?? (intervals.has('month') ? 'month' : 'year'))
            })
            .catch((err) => toast.error(aiErrorMessage(err)))
            .finally(() => !cancelled && setLoading(false))
        return () => {
            cancelled = true
        }
    }, [applySubscription, reloadPlans])

    /** Says what went wrong; a plan gone off sale also reloads the list. */
    const showBillingError = (err: unknown) => {
        toast.error(billingError(err))
        if (aiErrorCode(err) === 'AI_PACKAGE_UNAVAILABLE') setReloadPlans((n) => n + 1)
    }

    const current = subscription?.plan ?? null
    // The current plan's card shows the terms the subscriber is on, and is
    // shown even when that plan is no longer on sale (hidden or retired).
    const allPlans = useMemo(() => {
        if (!current) return plans
        if (plans.some((p) => p.id === current.id)) return plans.map((p) => (p.id === current.id ? { ...p, ...current } : p))
        const at = plans.findIndex((p) => p.billingInterval === current.billingInterval && monthly(p) > monthly(current))
        return at === -1 ? [...plans, current] : [...plans.slice(0, at), current, ...plans.slice(at)]
    }, [plans, current])
    const intervals = useMemo(() => Array.from(new Set(allPlans.map((p) => p.billingInterval))).sort(), [allPlans])
    const shown = useMemo(() => allPlans.filter((p) => p.billingInterval === billing), [allPlans, billing])
    const pastDue = subscription?.status === 'past_due'
    const scheduled = subscription?.scheduledChange ?? null

    // The card worth pointing at: the next plan up from the current one, or
    // the second plan for someone on none. "Coming soon" plans are skipped.
    const recommendedId = useMemo(() => {
        const ranked = shown.filter((p) => p.available).sort((a, b) => monthly(a) - monthly(b))
        if (current) return ranked.find((p) => monthly(p) > monthly(current))?.id ?? null
        return ranked.length > 1 ? ranked[1].id : null
    }, [shown, current])

    /** Off to Stripe, remembering the purchase so leaving without paying closes it. */
    const redirect = (url: string, purchaseId?: number) => {
        try {
            if (purchaseId) window.sessionStorage.setItem(PENDING_PURCHASE_KEY, String(purchaseId))
        } catch {
            // Without storage it is still closed by Stripe's back link.
        }
        window.location.href = url
    }

    const choosePlan = (plan: AIPlan) => {
        const change = changeFor(plan, subscription)
        if (change.kind === 'new' || !subscription) return startSubscription(plan)
        if (change.kind === 'current' || change.kind === 'blocked') return
        setPending({ plan, change })
    }

    const startSubscription = async (plan: AIPlan) => {
        setBusy(`plan-${plan.id}`)
        try {
            const out = await subscribeToPlan(plan.id)
            // The bank wants the upgrade confirmed: it is finished on Stripe,
            // and the plan changes once that payment goes through.
            if (out.requiresAction) {
                setAwaitingPayment({ url: out.url ?? null, planId: plan.id })
                if (out.url) {
                    // A blocked pop-up finishes it in this tab instead.
                    const opened = window.open(out.url, '_blank')
                    if (!opened) return redirect(out.url)
                    opened.opener = null
                }
                toast.info("Finish the payment on Stripe; your plan changes once it's paid.", { duration: 10000 })
                setBusy(null)
                return
            }
            if (out.url) return redirect(out.url, out.purchaseId)
            if (out.subscription !== undefined) applySubscription(out.subscription)
            toast.success(
                out.upgrade
                    ? `You're on ${plan.name}. The new tokens arrive as soon as the payment clears.${out.resumed ? ' Your plan will renew as usual.' : ''}`
                    : `You'll move to ${plan.name} when your plan renews.${out.resumed ? ' Your plan will renew as usual.' : ''}`,
            )
            setBusy(null)
            // The new tokens are credited when Stripe confirms the payment,
            // usually within seconds, so look again now and once more shortly.
            await reloadSubscription()
            if (out.upgrade) window.setTimeout(() => reloadSubscription(), 5000)
        } catch (err) {
            showBillingError(err)
            setBusy(null)
        }
    }

    const setCancelled = async (resume: boolean) => {
        setBusy(resume ? 'resume' : 'cancel')
        try {
            const { subscription: next } = await cancelAISubscription(resume)
            applySubscription(next)
            refreshBalance()
            toast.success(resume ? 'Your plan will renew as usual.' : `Your plan stays until ${formatDate(next?.currentPeriodEnd)}, then ends.`)
        } catch (err) {
            toast.error(aiErrorMessage(err))
        } finally {
            setBusy(null)
        }
    }

    const manageBilling = async () => {
        setBusy('portal')
        try {
            redirect((await openBillingPortal()).url)
        } catch (err) {
            toast.error(aiErrorMessage(err))
            setBusy(null)
        }
    }

    const buy = async (pkg: AIPackage) => {
        setBusy(`pkg-${pkg.id}`)
        try {
            const started = await checkoutAIPackage(pkg.id)
            redirect(started.url, started.purchaseId)
        } catch (err) {
            showBillingError(err)
            setBusy(null)
        }
    }

    const confirm = () => {
        if (!pending) return
        if ('cancel' in pending) setCancelled(false)
        else startSubscription(pending.plan)
        setPending(null)
    }

    const planButton = (plan: AIPlan) => {
        const change = changeFor(plan, subscription)
        if (current?.id === plan.id || change.kind === 'current') {
            return (
                <Button variant='outline' className='w-full rounded-full' disabled>
                    Your current plan
                </Button>
            )
        }
        // Not possible from here (an unpaid plan, a cheaper plan billed more
        // often): the button stays, disabled, with the reason under it.
        const blocked = change.kind === 'blocked' || pastDue
        const label = !plan.available
            ? 'Coming soon'
            : change.kind === 'new'
              ? `Get ${plan.name}`
              : change.kind === 'upgrade'
                ? `Upgrade to ${plan.name}`
                : `Switch to ${plan.name}`
        return (
            <>
                <Button
                    className='w-full rounded-full'
                    variant={plan.id === recommendedId ? 'default' : 'outline'}
                    disabled={!plan.available || !enabled || busy !== null || blocked}
                    onClick={() => choosePlan(plan)}>
                    {busy === `plan-${plan.id}` ? <Spinner variant='circle' className='h-4 w-4' /> : label}
                </Button>
                {blocked && plan.available && change.message && <p className='mt-2 text-center text-xs text-muted-foreground'>{change.message}</p>}
            </>
        )
    }

    return (
        <>
                <div className='mx-auto w-full max-w-6xl pt-6 pb-16'>
                    <h1 className='text-center text-2xl font-semibold sm:text-3xl'>
                        {tab === 'credits' ? 'Top up credits' : subscription ? 'Change your plan' : 'Choose your plan'}
                    </h1>
                    <p className='mt-2 text-center text-sm text-muted-foreground'>
                        {tab === 'credits'
                            ? 'One-off token packs, added to your balance on top of any plan.'
                            : 'Every plan gives you its tokens for the billing period. Unused plan tokens do not carry over.'}
                    </p>

                    <div className='mt-6 flex justify-center'>
                        <div className='inline-flex rounded-full bg-muted p-1 text-sm'>
                            {(['plans', 'credits'] as PricingTab[]).map((value) => (
                                <button
                                    key={value}
                                    type='button'
                                    onClick={() => onTabChange(value)}
                                    className={cn(
                                        'cursor-pointer rounded-full px-4 py-1.5 transition-colors',
                                        tab === value ? 'bg-background font-medium text-foreground shadow-sm' : 'text-muted-foreground',
                                    )}>
                                    {value === 'plans' ? 'Plans' : 'Top up credits'}
                                </button>
                            ))}
                        </div>
                    </div>

                    {loading ? (
                        <div className='flex justify-center py-24'>
                            <Spinner variant='circle' />
                        </div>
                    ) : !enabled ? (
                        <p className='mt-12 text-center text-sm text-muted-foreground'>
                            Payments are not enabled on this environment. Your administrator can add tokens to your account directly.
                        </p>
                    ) : tab === 'plans' ? (
                        <>
                            {pastDue && (
                                <div className='mx-auto mt-6 flex max-w-2xl items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900'>
                                    <TriangleAlert className='mt-0.5 h-4 w-4 shrink-0' />
                                    <div className='flex flex-col items-start gap-2'>
                                        <span>
                                            Your last payment did not go through, so this period's tokens are on hold until it does. You can change
                                            plan once it is paid.
                                        </span>
                                        <Button size='sm' variant='outline' disabled={busy !== null} onClick={manageBilling}>
                                            {busy === 'portal' ? <Spinner variant='circle' className='h-4 w-4' /> : 'Update your payment method'}
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {awaitingPayment && (
                                <div className='mx-auto mt-6 flex max-w-2xl items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900'>
                                    <CreditCard className='mt-0.5 h-4 w-4 shrink-0' />
                                    <span>
                                        Finish the payment on Stripe; your plan changes once it's paid.{' '}
                                        {awaitingPayment.url && (
                                            <a href={awaitingPayment.url} target='_blank' rel='noopener noreferrer' className='font-medium underline'>
                                                Open the payment page
                                            </a>
                                        )}
                                    </span>
                                </div>
                            )}

                            {scheduled && (
                                <p className='mx-auto mt-6 max-w-2xl rounded-lg border border-blue-200 bg-blue-50 p-3 text-center text-sm text-blue-900'>
                                    From {formatDate(scheduled.from ?? subscription?.currentPeriodEnd)} your plan will be{' '}
                                    {formatMoney(scheduled.price, scheduled.currency)} for {formatTokens(scheduled.tokenAmount)} tokens
                                    {scheduled.billingInterval ? ` every ${INTERVAL_LABEL[scheduled.billingInterval]}` : ''}.
                                </p>
                            )}

                            {intervals.length > 1 && (
                                <div className='mt-6 flex justify-center'>
                                    <div className='inline-flex rounded-full border p-1 text-xs'>
                                        {intervals.map((value) => (
                                            <button
                                                key={value}
                                                type='button'
                                                onClick={() => setBilling(value)}
                                                className={cn(
                                                    'cursor-pointer rounded-full px-3 py-1',
                                                    billing === value ? 'bg-foreground text-background' : 'text-muted-foreground',
                                                )}>
                                                {value === 'month' ? 'Monthly' : 'Yearly'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {shown.length === 0 ? (
                                <p className='mt-12 text-center text-sm text-muted-foreground'>No plans are on offer at the moment.</p>
                            ) : (
                                <div
                                    className={cn(
                                        'mt-8 grid grid-cols-1 gap-4',
                                        shown.length === 2 && 'md:grid-cols-2',
                                        shown.length >= 3 && 'md:grid-cols-2 lg:grid-cols-3',
                                    )}>
                                    {shown.map((plan) => {
                                        const { prefix, value } = splitMoney(plan.price, plan.currency)
                                        const isCurrent = current?.id === plan.id
                                        return (
                                            <div
                                                key={plan.id}
                                                className={cn(
                                                    'flex flex-col rounded-2xl border p-6',
                                                    plan.id === recommendedId && 'border-transparent shadow-[0_18px_50px_-12px_rgba(0,0,0,0.35)]',
                                                )}>
                                                <div className='flex items-center justify-between gap-2'>
                                                    <h3 className='text-2xl font-semibold'>{plan.name}</h3>
                                                    {isCurrent ? (
                                                        <Badge variant='secondary'>Current</Badge>
                                                    ) : plan.id === recommendedId ? (
                                                        <Badge className='bg-black text-white'>Recommended</Badge>
                                                    ) : null}
                                                </div>
                                                <div className='mt-6 flex items-start gap-1'>
                                                    <span className='mt-1 text-lg text-muted-foreground'>{prefix}</span>
                                                    <span className='text-5xl font-semibold tracking-tight'>{value}</span>
                                                    <span className='mt-2 ml-1 text-xs leading-tight text-muted-foreground'>
                                                        {plan.currency} /<br />
                                                        {INTERVAL_LABEL[plan.billingInterval]}
                                                    </span>
                                                </div>
                                                <p className='mt-4 min-h-6 font-medium'>{plan.tagline || plan.description || ''}</p>
                                                <div className='mt-5'>{planButton(plan)}</div>
                                                {isCurrent && subscription?.currentPeriodEnd && (
                                                    <p className='mt-2 text-center text-xs text-muted-foreground'>
                                                        {subscription.cancelAtPeriodEnd ? 'Ends' : 'Renews'} {formatDate(subscription.currentPeriodEnd)}
                                                    </p>
                                                )}
                                                <ul className='mt-6 space-y-3 text-sm'>
                                                    <li className='flex items-start gap-3'>
                                                        <Coins className='mt-0.5 h-4 w-4 shrink-0' />
                                                        <span className='font-medium'>
                                                            {formatTokens(plan.tokenAmount)} AI tokens every {INTERVAL_LABEL[plan.billingInterval]}
                                                        </span>
                                                    </li>
                                                    {plan.features.map((feature) => (
                                                        <li key={feature} className='flex items-start gap-3'>
                                                            <Check className='mt-0.5 h-4 w-4 shrink-0' />
                                                            <span>{feature}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}

                            {subscription && (
                                <div className='mt-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-muted-foreground'>
                                    <span>
                                        {subscription.cancelAtPeriodEnd
                                            ? `Your plan ends on ${formatDate(subscription.currentPeriodEnd)}.`
                                            : `Your plan renews on ${formatDate(subscription.currentPeriodEnd)}.`}
                                    </span>
                                    {subscription.cancelAtPeriodEnd ? (
                                        <Button variant='link' className='h-auto p-0' disabled={busy !== null} onClick={() => setCancelled(true)}>
                                            {busy === 'resume' ? <Spinner variant='circle' className='h-4 w-4' /> : 'Keep my plan'}
                                        </Button>
                                    ) : (
                                        <Button variant='link' className='h-auto p-0 text-muted-foreground' disabled={busy !== null} onClick={() => setPending({ cancel: true })}>
                                            {busy === 'cancel' ? <Spinner variant='circle' className='h-4 w-4' /> : 'Cancel plan'}
                                        </Button>
                                    )}
                                    <Button variant='link' className='h-auto p-0' disabled={busy !== null} onClick={manageBilling}>
                                        <CreditCard className='h-4 w-4' />
                                        {busy === 'portal' ? <Spinner variant='circle' className='h-4 w-4' /> : 'Manage billing'}
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : packages.length === 0 ? (
                        <p className='mt-12 text-center text-sm text-muted-foreground'>No credit packs are on sale at the moment.</p>
                    ) : (
                        <div className='mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                            {packages.map((pkg) => {
                                const { prefix, value } = splitMoney(pkg.price, pkg.currency)
                                const discounted = Number(pkg.listPrice) > Number(pkg.price)
                                return (
                                    <div key={pkg.id} className='flex flex-col rounded-2xl border p-6'>
                                        <div className='flex items-center justify-between gap-2'>
                                            <h3 className='text-xl font-semibold'>{pkg.name}</h3>
                                            {pkg.promotion && <Badge className='bg-green-600'>{pkg.promotion.name}</Badge>}
                                        </div>
                                        {pkg.description && <p className='mt-1 text-sm text-muted-foreground'>{pkg.description}</p>}
                                        <div className='mt-5 flex items-baseline gap-2'>
                                            <span className='text-muted-foreground'>{prefix}</span>
                                            <span className='text-4xl font-semibold tracking-tight'>{value}</span>
                                            {discounted && (
                                                <span className='text-sm text-muted-foreground line-through'>{formatMoney(pkg.listPrice, pkg.currency)}</span>
                                            )}
                                        </div>
                                        <ul className='mt-5 space-y-2 text-sm'>
                                            <li className='flex items-center gap-3'>
                                                <Coins className='h-4 w-4 shrink-0' />
                                                <span className='font-medium'>{formatTokens(pkg.tokenAmount)} tokens</span>
                                            </li>
                                            {pkg.bonusTokens > 0 && (
                                                <li className='flex items-center gap-3 text-green-700'>
                                                    <Check className='h-4 w-4 shrink-0' />+ {formatTokens(pkg.bonusTokens)} bonus tokens
                                                </li>
                                            )}
                                            <li className='flex items-center gap-3 text-muted-foreground'>
                                                <Check className='h-4 w-4 shrink-0' />
                                                {pkg.validityDays ? `Valid for ${pkg.validityDays} days` : 'Never expire'}
                                            </li>
                                        </ul>
                                        <Button className='mt-6 w-full rounded-full' disabled={busy !== null} onClick={() => buy(pkg)}>
                                            {busy === `pkg-${pkg.id}` ? (
                                                <Spinner variant='circle' className='h-4 w-4' />
                                            ) : (
                                                `Buy ${formatTokens(pkg.totalTokens)} tokens`
                                            )}
                                        </Button>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>

            <AlertDialog open={pending !== null} onOpenChange={(next) => !next && setPending(null)}>
                <AlertDialogContent>
                    {pending && 'cancel' in pending ? (
                        <AlertDialogHeader>
                            <AlertDialogTitle>Cancel your plan?</AlertDialogTitle>
                            <AlertDialogDescription>
                                You keep {current?.name ?? 'your plan'} until {formatDate(subscription?.currentPeriodEnd)}, and can use its tokens until
                                then. After that it won't renew and any unused plan tokens expire. Top-up tokens you bought stay yours.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                    ) : pending?.change.kind === 'upgrade' ? (
                        <AlertDialogHeader>
                            <AlertDialogTitle>Upgrade to {pending.plan.name}?</AlertDialogTitle>
                            {/* An upgrade starts a new period today; the unused share of the current plan comes off the price. */}
                            {(() => {
                                const { plan, change } = pending
                                const discount = Number(change.discount ?? 0)
                                const charge = Number(change.chargeNow ?? plan.price - discount)
                                const percent = current && current.price > 0 ? Math.min(100, Math.round((discount / current.price) * 100)) : 0
                                const tokens = Number(change.unusedTokens ?? 0)
                                return (
                                    <AlertDialogDescription>
                                        {discount > 0
                                            ? `You pay ${formatMoney(charge, plan.currency)} now (${formatMoney(plan.price, plan.currency)} plan − ${formatMoney(discount, plan.currency)} for the ${percent}% of your current plan you haven't used${tokens > 0 ? `, ${formatTokens(tokens)} tokens` : ''}). `
                                            : `You pay ${formatMoney(charge, plan.currency)} now. `}
                                        Your new plan starts today and renews on {formatDate(renewsFromToday(plan.billingInterval))}. It gives you{' '}
                                        {formatTokens(plan.tokenAmount)} tokens every {INTERVAL_LABEL[plan.billingInterval]}, in place of what is left of your
                                        current plan's tokens; top-up tokens stay yours.
                                        {subscription?.cancelAtPeriodEnd &&
                                            ` Your plan is set to end on ${formatDate(subscription.currentPeriodEnd)}; upgrading keeps it going, and it renews as usual.`}
                                    </AlertDialogDescription>
                                )
                            })()}
                        </AlertDialogHeader>
                    ) : pending ? (
                        <AlertDialogHeader>
                            <AlertDialogTitle>Switch to {pending.plan.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                                You won't be charged now. From your renewal on {formatDate(pending.change.effectiveFrom ?? subscription?.currentPeriodEnd)} you'll pay{' '}
                                {formatMoney(pending.plan.price, pending.plan.currency)} and get {formatTokens(pending.plan.tokenAmount)} tokens every{' '}
                                {INTERVAL_LABEL[pending.plan.billingInterval]}.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                    ) : null}
                    <AlertDialogFooter>
                        <AlertDialogCancel>{pending && 'cancel' in pending ? 'Keep plan' : 'Not now'}</AlertDialogCancel>
                        <AlertDialogAction onClick={confirm}>
                            {pending && 'cancel' in pending ? 'Cancel plan' : pending?.change.kind === 'upgrade' ? 'Upgrade and pay' : 'Switch plan'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}

export default PricingPage
